import React, { useEffect, useState, useCallback } from 'react';
import { Trash2, AlertCircle, RefreshCw, Eye, EyeOff } from 'lucide-react';
import { apiService } from '../../services/api';
import { ProcessorMetadata } from '../../types/processor';
import { ProcessorCard } from './ProcessorCard';
import { usePipelineBuilderStore } from '../../store/pipelineBuilderStore';

const SHOW_DESCRIPTIONS_KEY = 'pipelineBuilder.showDescriptions';

export const ProcessorPalette: React.FC = () => {
  const [processors, setProcessors] = useState<ProcessorMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  
  // Initialize from localStorage, default to true
  const [showDescriptions, setShowDescriptions] = useState(() => {
    const saved = localStorage.getItem(SHOW_DESCRIPTIONS_KEY);
    return saved !== null ? saved === 'true' : true;
  });

  const { nodes, setNodes, setEdges} = usePipelineBuilderStore();

  // Save to localStorage whenever showDescriptions changes
  useEffect(() => {
    localStorage.setItem(SHOW_DESCRIPTIONS_KEY, String(showDescriptions));
  }, [showDescriptions]);

  // Fetch processors on mount
  const fetchProcessors = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiService.getProcessors();
      // Sort alphabetically by processor_name
      const sorted = [...data].sort((a, b) =>
        a.processor_name.localeCompare(b.processor_name)
      );
      setProcessors(sorted);
    } catch (err: any) {
      console.error('Failed to fetch processors:', err);
      setError(err.response?.data?.detail || 'Failed to load processors. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProcessors();
  }, [fetchProcessors]);


  // Handle drag start
  const handleDragStart = useCallback((e: React.DragEvent, processor: ProcessorMetadata) => {
    e.dataTransfer.setData(
      'application/reactflow',
      JSON.stringify({
        type: 'processor',
        processor,
      })
    );
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  // Handle Clear All
  const handleClearAll = useCallback(() => {
    // Count non-system nodes
    const customNodesCount = nodes.filter((node) => node.id !== 'input').length;

    if (customNodesCount === 0) {
      // Nothing to clear
      return;
    }

    if (customNodesCount > 0) {
      // Show confirmation modal
      setShowClearConfirm(true);
    }
  }, [nodes]);

  const confirmClearAll = useCallback(() => {
    // Keep only the system input node
    const systemInputNode = nodes.find((node) => node.id === 'input');
    setNodes(systemInputNode ? [systemInputNode] : []);
    setEdges([]);
    setShowClearConfirm(false);
  }, [nodes, setNodes, setEdges]);

  const cancelClearAll = useCallback(() => {
    setShowClearConfirm(false);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
        <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-gray-100">
          Processors
        </h3>
        <div className="space-y-4" data-testid="skeleton-loaders">
          {/* Skeleton loaders */}
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="animate-pulse bg-gray-200 dark:bg-gray-800 rounded-lg h-24"
            />
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
        <h3 className="font-bold text-lg mb-4 text-gray-900 dark:text-gray-100">
          Processors
        </h3>
        <div className="flex flex-col items-center justify-center py-8">
          <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-4">
            {error}
          </p>
          <button
            onClick={fetchProcessors}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto"
        data-testid="processor-palette"
      >
        {/* Header with Clear All */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg text-gray-900 dark:text-gray-100">
            Processors
          </h3>
          <button
            onClick={handleClearAll}
            disabled={nodes.filter((n) => n.id !== 'input').length === 0}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded transition-colors"
            data-testid="clear-all-button"
            title="Clear All"
          >
            <Trash2 className="w-3 h-3" />
            Clear
          </button>
        </div>

        {/* Instruction */}
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
          Drag processors onto the canvas to build your pipeline.
        </p>

        {/* Toggle Descriptions Button */}
        <button
          onClick={() => setShowDescriptions(!showDescriptions)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-4 text-sm font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-md transition-colors border border-gray-300 dark:border-gray-600"
        >
          {showDescriptions ? (
            <>
              <EyeOff className="w-4 h-4" />
              Hide Descriptions
            </>
          ) : (
            <>
              <Eye className="w-4 h-4" />
              Show Descriptions
            </>
          )}
        </button>

        {/* Processor Cards (no groups) */}
        <div>
          {processors.map((processor) => (
            <ProcessorCard
              key={processor.processor_name}
              processor={processor}
              onDragStart={handleDragStart}
              showDescription={showDescriptions}
            />
          ))}
        </div>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={cancelClearAll}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Clear All Processors?
              </h3>
            </div>

            {/* Content */}
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              This will remove all processors from the canvas (except the System Input node).
              This action cannot be undone.
            </p>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelClearAll}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmClearAll}
                className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

